import {Box, Grid, TextField, Typography, MenuItem, Select} from '@mui/material';
import PropTypes from 'prop-types';
import {Controller, useFormContext} from 'react-hook-form';

import {TIMEZONES} from '../form/domain';
import {fieldRenderers} from '../form/fieldRenderers';
import ApiParameterTypeIcon from '../ParameterIcon/ParameterIcon';

// eslint-disable-next-line react/prop-types
const SingleParameterContainer = ({children, ...rest}) => (
  <Grid item xs={12} md={2} container alignItems="center" mt={1} {...rest}>
    {children}
  </Grid>
);

const SingleParameterRow = ({param}) => {
  const {control, watch} = useFormContext();

  const commonSx = {flex: 1, width: '100%'};

  const renderField = () => {
    // 1️⃣ Suggested values override everything
    if (param.suggestedValues?.length) {
      return (
        <Controller
          name={`parameters.${param.name}`}
          control={control}
          render={({field}) => (
            <Select {...field} size="small" sx={commonSx}>
              {param.suggestedValues.map(v => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      );
    }

    // 2️⃣ Composite parameters (explicit & readable)
    if (param.type === 'DateTimeZone') {
      return (
        <Grid container gap={1} flexWrap="nowrap">
          <Grid item xs={7}>
            <Controller name={`parameters.${param.name}.datetime`} control={control} render={({field}) => <TextField {...field} size="small" fullWidth />} />
          </Grid>

          <Grid item xs={5}>
            <Controller
              name={`parameters.${param.name}.timezone`}
              control={control}
              render={({field}) => (
                <Select {...field} size="small" fullWidth>
                  {TIMEZONES.map(tz => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>
        </Grid>
      );
    }

    if (param.type === 'Duration') {
      return (
        <Grid container gap={1} flexWrap="nowrap">
          <Grid item xs={4}>
            <Controller name={`parameters.${param.name}.days`} control={control} render={({field}) => <TextField {...field} type="number" label="Days" size="small" />} />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name={`parameters.${param.name}.hours`}
              control={control}
              render={({field}) => (
                <Select {...field} size="small">
                  {[...Array(24).keys()].map(h => (
                    <MenuItem key={h} value={h}>
                      {h}h
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>

          <Grid item xs={4}>
            <Controller
              name={`parameters.${param.name}.minutes`}
              control={control}
              render={({field}) => (
                <Select {...field} size="small">
                  {[...Array(60).keys()].map(m => (
                    <MenuItem key={m} value={m}>
                      {m}m
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Grid>
        </Grid>
      );
    }

    const Renderer = fieldRenderers[param.type];
    if (!Renderer) return null;

    return (
      <Controller
        name={`parameters.${param.name}`}
        control={control}
        render={({field}) =>
          Renderer({
            field,
            sx: commonSx
          })
        }
      />
    );
  };

  return (
    <Box sx={{py: 1, borderBottom: th => `1px solid ${th.palette.divider}`}}>
      <Grid
        container
        gap={{xs: 1, md: 2}}
        sx={{
          my: {xs: 1.5, sm: 0.5},
          flexDirection: {xs: 'column', md: 'row'},
          alignItems: {xs: 'flex-start', md: 'center'}
        }}
      >
        <SingleParameterContainer md={5}>
          <ApiParameterTypeIcon type={param.type} />
          <Typography fontWeight="bold">{param.name}</Typography>
        </SingleParameterContainer>

        <SingleParameterContainer md={4}>{renderField()}</SingleParameterContainer>

        <SingleParameterContainer>
          <Typography variant="body2" color="text.secondary">
            {param.description}
          </Typography>
        </SingleParameterContainer>
      </Grid>
    </Box>
  );
};

SingleParameterRow.propTypes = {
  param: PropTypes.object.isRequired
};

export default SingleParameterRow;
